/**
 * The first screen. One button.
 *
 * ## Why the microphone is not requested until the tap
 *
 * Two reasons, and both are hard requirements rather than preferences. A permission prompt
 * that appears before the player has any idea what the app does gets denied, and a denied
 * microphone on iPadOS is an eight-tap recovery behind a menu nobody knows about. And the
 * same gesture is the only moment Safari will let an `AudioContext` be resumed — so the
 * tap that asks for the microphone is also the tap that unlocks the audio graph. Asking
 * earlier would break both.
 *
 * ## The one line of copy
 *
 * *"This app listens to your piano. Audio stays on this device."* That is the whole
 * privacy story and it is true — the recogniser runs in an AudioWorklet and only chord
 * symbols ever leave the device. It is one line because the person reading it is standing
 * at a piano wanting to play, and a paragraph would be read by nobody and reassure no one.
 *
 * It is also the reason the line disappears On the Go: a promise about audio, on a screen
 * that will never open a microphone, is not reassurance. It is a reminder that this app
 * could listen — which is the opposite of what it is there for.
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { IntentionInput } from './IntentionInput.js';
import { HarmonySlider } from './HarmonySlider.js';
import { ModeToggle } from '../system/ModeToggle.js';
import { useCalmMotion } from '../system/motionPreference.js';
import { CALM_FADE } from '../graph/motion.js';
import type { SessionMode } from '../system/sessionView.js';

export interface HomeScreenProps {
  intention?: string;
  /**
   * Absent hides the field entirely — the same doctrine as every other capability on this
   * screen: a component does not ask what the app is set up as, it asks what it was
   * handed. An intention is a sentence written for a model to read, so with the AI
   * companion off there is nothing on the other end of it, and a field that quietly went
   * nowhere would be the app pretending to listen.
   */
  onIntentionChange?: (value: string) => void;
  /**
   * Starts the session. At the piano this requests the microphone and unlocks the
   * `AudioContext`, so it must be called from the tap; On the Go it does neither.
   */
  onStart: () => void;
  /** True while the permission prompt is up. */
  starting?: boolean;
  /** Absent means `'piano'`. */
  mode?: SessionMode;
  /** Absent hides the toggle entirely — a control with nowhere to go is worse than none. */
  onModeChange?: (mode: SessionMode) => void;
  /** False disables the piano half. See `ModeToggle`. */
  pianoAvailable?: boolean;
  /**
   * Open the sketchbook. Absent hides the link — and absent is the normal case under a
   * fixture, where there is no router to navigate with.
   *
   * It is here rather than on the session screen because this is the one moment nobody is
   * playing. Mid-session the graph is the app, and a second destination in the chrome above
   * it would be a way of leaving the thing you came to do.
   */
  onOpenSketchbook?: () => void;
  /**
   * Take the ten-second first run. Absent hides the line, which is the normal case: On
   * the Go, and at the piano for anybody whose device has been tuned before.
   *
   * The decision of whether to offer it belongs to `SessionPage`, not here — it is a
   * question for IndexedDB, and this screen has to stay a pure function of its props so
   * that every fixture and every one of the fifteen states can render it.
   */
  onCalibrate?: () => void;
  /** The session's `state.adventurousness`, 0..1. Read only when the setter is present. */
  adventurousness?: number;
  /**
   * Set how familiar the harmony should be. Absent hides the slider, the same doctrine as
   * every other capability on this screen; `SessionPage` always hands it over.
   *
   * It is here as well as in the settings drawer because this is the one moment the player
   * is deciding what kind of session they want, and a setting that shapes every set the
   * engine offers should be met before the first set rather than found after it. It is the
   * same setting rather than a second one: the caller dispatches `SET_ADVENTUROUSNESS`,
   * exactly as the drawer does. Not companion-gated — the built-in engine reads it as its
   * band selector, so it means as much with the model off as on.
   */
  onAdventurousnessChange?: (value: number) => void;
}

export function HomeScreen({
  intention = '',
  onIntentionChange,
  onStart,
  starting = false,
  mode = 'piano',
  onModeChange,
  pianoAvailable = true,
  onOpenSketchbook,
  onCalibrate,
  adventurousness = 0.5,
  onAdventurousnessChange,
}: HomeScreenProps): React.JSX.Element {
  const calm = useCalmMotion();
  const [pressed, setPressed] = useState(false);
  const onTheGo = mode === 'on-the-go';

  return (
    <section className="hc-home" data-testid="hc-home">
      {/*
        Transform-only entry, with no opacity in it. This is the first screen and it holds
        the app's single call to action, so it must be readable even if the animation never
        runs — which happens whenever the document timeline is paused, as it is for a tab
        that loads while backgrounded. An entry fade there would leave someone looking at
        an empty dark rectangle until they touched it.
      */}
      <motion.div
        className="hc-home__stack"
        initial={calm ? false : { y: 10 }}
        animate={{ y: 0 }}
        transition={calm ? CALM_FADE : { type: 'spring', stiffness: 300, damping: 32 }}
      >
        <h1 className="hc-home__title">Harmonic Companion</h1>
        <p className="hc-home__blurb">
          {onTheGo
            ? 'Pick a chord. It shows you where you could go next.'
            : 'Play something. It listens, and shows you where you could go next.'}
        </p>

        {onModeChange ? (
          <ModeToggle mode={mode} onChange={onModeChange} pianoAvailable={pianoAvailable} />
        ) : null}

        {onAdventurousnessChange ? (
          <HarmonySlider value={adventurousness} onChange={onAdventurousnessChange} />
        ) : null}

        {onIntentionChange ? (
          <IntentionInput value={intention} onChange={onIntentionChange} onSubmit={onStart} />
        ) : null}

        <button
          type="button"
          className="hc-start"
          data-testid="hc-start"
          disabled={starting && !onTheGo}
          onPointerDown={() => setPressed(true)}
          onPointerUp={() => setPressed(false)}
          onPointerLeave={() => setPressed(false)}
          onClick={onStart}
          style={{ transform: pressed && !calm ? 'scale(0.985)' : undefined }}
        >
          {onTheGo ? 'Start exploring' : starting ? 'Waiting for the microphone…' : 'Start playing'}
        </button>

        {onTheGo ? null : (
          <p className="hc-home__privacy" data-testid="hc-privacy-line">
            This app listens to your piano. Audio stays on this device.
          </p>
        )}

        {/*
          One line, and only for a device that has never been tuned. It shares the
          sketchbook link's styling deliberately: both are quiet ways out of the single
          call to action above them, and this screen's calm is the requirement — a
          calibration *card* here would compete with the one button that matters.
        */}
        {onTheGo || !onCalibrate ? null : (
          <button
            type="button"
            className="hc-home__sketchbook"
            data-testid="hc-calibrate"
            onClick={onCalibrate}
          >
            First time in this room? Calibrate the listening (10 seconds)
          </button>
        )}

        {onOpenSketchbook ? (
          <button
            type="button"
            className="hc-home__sketchbook"
            data-testid="hc-open-sketchbook"
            onClick={onOpenSketchbook}
          >
            Sketchbook
          </button>
        ) : null}
      </motion.div>
    </section>
  );
}
